import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExploreCard } from 'modules/explore-games/components/explore-card';
import { TableBar } from 'modules/explore-games/pages/components/table-bar';

import DesktopLayout from 'shared/components/layout/desktop-layout';
import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';
import { CHAINS_BY_ID } from 'shared/config/config.constants';
import { SUPPORTED_CHAIN_IDS } from 'shared/config/config.types';
import { CHAINS_TOKENS_CONFIG } from 'shared/constants';
import { DEADLINE_BUFFER_MINUTES } from 'shared/constants/constants';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import { useGamesStore } from 'shared/store/games-store';
import { useUIStore } from 'shared/store/uiStore';
import { RoomInfo } from 'shared/types/app-types';
import { CurrencyFilter, SortOrder } from 'shared/types/games.types';
import { isMocked } from 'shared/utils/isMocked';
import { epochToLocalUTC } from 'shared/utils/time';

import { Dir, ExploreCardSkeleton, multiSort } from './explore-games.utils';

export const ExploreGames = () => {
  const { t } = useTranslation();
  const { chain, chainId: walletChainId, isConnected } = useAccount();
  const { connectWalletModal } = useUIStore();

  // 1) Choose effective chain: wallet if available, else BASE
  const effectiveChain = useMemo(() => chain ?? CHAINS_BY_ID[SUPPORTED_CHAIN_IDS.BASE], [chain]);
  const effectiveChainId = useMemo(
    () => (!isMocked ? effectiveChain?.id : SUPPORTED_CHAIN_IDS.ARBITRUM),
    [effectiveChain],
  );

  // 2) Public client bound to effective chain
  const publicClient = usePublicClient();

  const {
    startStore,
    activeRooms,
    playedRoomsFirstBatch,
    loading,
    clearStore,
    loadMorePlayedRooms,
  } = useGamesStore();

  const [activeRoomsFiltered, setActiveRoomsFiltered] = useState<RoomInfo[]>([]);
  const [filteredPlayedRooms, setFilteredPlayedRooms] = useState<RoomInfo[]>([]);

  const [sorting, setSorting] = useState({
    search: '',
    currency: CurrencyFilter.ALL,
    roomId: SortOrder.DESC,
    betAmount: SortOrder.DEFAULT,
  });

  const bottomRef = useRef<HTMLDivElement>(null);

  // Reset sorting when chain changes
  useEffect(() => {
    setSorting({
      search: '',
      currency: CurrencyFilter.ALL,
      roomId: SortOrder.DESC,
      betAmount: SortOrder.DEFAULT,
    });
  }, [effectiveChainId]);

  // 3) Stable token symbol → address map for the effective chain
  const tokenSymbolToAddressMap = useMemo(() => {
    const list = effectiveChain?.name ? CHAINS_TOKENS_CONFIG[effectiveChain.name] : undefined;
    if (!list) return {};
    return list.reduce<Record<string, string>>((acc, token) => {
      acc[token.symbol] = token.address.toLowerCase();
      return acc;
    }, {});
  }, [effectiveChain?.name]);

  // 4) Derive Active rooms
  useEffect(() => {
    if (!activeRooms?.length) {
      setActiveRoomsFiltered([]);
      return;
    }

    let rooms = activeRooms.filter((room) => room.data.playerB);

    rooms = rooms.filter((room) => {
      const deadlineDate = epochToLocalUTC(room.deadline);
      // FIX: DEADLINE_BUFFER_MINUTES is in minutes → convert to ms
      const deadlineWithBuffer = new Date(
        deadlineDate.getTime() + DEADLINE_BUFFER_MINUTES * 60_000,
      );
      return deadlineWithBuffer.getTime() >= Date.now();
    });

    if (sorting.currency !== CurrencyFilter.ALL) {
      const tokenAddress = tokenSymbolToAddressMap[sorting.currency];
      if (tokenAddress) {
        rooms = rooms.filter((room) => room.data.token.toLowerCase() === tokenAddress);
      }
    }

    if (sorting.search) {
      const query = sorting.search.toLowerCase();
      rooms = rooms.filter(
        (room) =>
          room.data.roomId.toString().includes(query) ||
          room.data.playerA.toLowerCase().includes(query) ||
          (room.data.playerB && room.data.playerB.toLowerCase().includes(query)),
      );
    }

    rooms = [...rooms].sort((a, b) => {
      if (sorting.roomId === SortOrder.ASC || sorting.roomId === SortOrder.DESC) {
        const A = Number(a.data.roomId ?? 0);
        const B = Number(b.data.roomId ?? 0);
        return sorting.roomId === SortOrder.ASC ? A - B : B - A;
      }
      if (sorting.betAmount === SortOrder.ASC || sorting.betAmount === SortOrder.DESC) {
        const A = Number(a.data.bet ?? 0);
        const B = Number(b.data.bet ?? 0);
        return sorting.betAmount === SortOrder.ASC ? A - B : B - A;
      }
      return 0;
    });

    setActiveRoomsFiltered(rooms);
  }, [activeRooms, sorting, tokenSymbolToAddressMap]);

  // 5) Derive Played rooms
  useEffect(() => {
    if (!playedRoomsFirstBatch?.length) {
      setFilteredPlayedRooms([]);
      return;
    }

    let rooms = [...playedRoomsFirstBatch];

    if (sorting.currency !== CurrencyFilter.ALL) {
      const tokenAddress = tokenSymbolToAddressMap[sorting.currency];
      if (tokenAddress) {
        rooms = rooms.filter((room) => room.data.token.toLowerCase() === tokenAddress);
      }
    }

    if (sorting.search) {
      const query = sorting.search.toLowerCase();
      rooms = rooms.filter(
        (room) =>
          room.data.roomId.toString().includes(query) ||
          room.data.playerA.toLowerCase().includes(query) ||
          (room.data.playerB && room.data.playerB.toLowerCase().includes(query)),
      );
    }

    const betOrder: Dir | undefined =
      sorting.betAmount === SortOrder.ASC || sorting.betAmount === SortOrder.DESC
        ? sorting.betAmount === SortOrder.ASC
          ? 'asc'
          : 'desc'
        : undefined;

    const idOrder: Dir =
      sorting.roomId === SortOrder.ASC || sorting.roomId === SortOrder.DESC
        ? sorting.roomId === SortOrder.ASC
          ? 'asc'
          : 'desc'
        : 'desc';

    const getters: Array<(r: RoomInfo) => number> = [];
    const dirs: Dir[] = [];
    if (betOrder) {
      getters.push((r) => Number(r.data.bet ?? 0));
      dirs.push(betOrder);
    }
    getters.push((r) => Number(r.data.roomId ?? 0));
    dirs.push(idOrder);

    rooms = rooms.sort(multiSort(getters, dirs));
    setFilteredPlayedRooms(rooms);
  }, [playedRoomsFirstBatch, sorting, tokenSymbolToAddressMap]);

  // 6) Start/Clear games store only when effective chain changes
  const lastStartedChainRef = useRef<number | null>(null);

  // Clear on unmount
  useEffect(() => {
    return () => {
      clearStore();
      lastStartedChainRef.current = null;
    };
  }, [clearStore]);

  // Clear when chain actually changes
  useEffect(() => {
    if (
      lastStartedChainRef.current !== null &&
      effectiveChainId &&
      lastStartedChainRef.current !== effectiveChainId
    ) {
      clearStore();
      lastStartedChainRef.current = null;
    }
  }, [effectiveChainId, clearStore]);

  // Start once per effective chain — clear first to avoid stale data and satisfy tests
  useEffect(() => {
    if (!effectiveChainId || !publicClient) return;
    if (lastStartedChainRef.current === effectiveChainId) return;

    // Clear before starting to ensure a clean state on first mount / chain switch
    clearStore();
    startStore(publicClient);
    lastStartedChainRef.current = effectiveChainId;
  }, [effectiveChainId, publicClient, startStore, clearStore]);

  const bottomRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !sorting.search && sorting.currency === CurrencyFilter.ALL) {
            loadMorePlayedRooms();
          }
        },
        { root: null, rootMargin: '0px 0px 200px 0px', threshold: 0.1 },
      );

      observer.observe(node);
      return () => observer.disconnect();
    },
    [sorting.search, sorting.currency, loadMorePlayedRooms],
  );

  if (connectWalletModal.show && !isConnected) {
    return (
      <ConnectWallet title={'common.connectWallet'} description={'pages.dailyQuests.connectDesc'} />
    );
  }

  return (
    <DesktopLayout title={t('pages.explore')} key={effectiveChainId ?? walletChainId}>
      <div className="flex max-w-[1920px] flex-col justify-center sm:mx-auto">
        <div className="mb-6">
          <TableBar sorting={sorting} handleSetSorting={setSorting} />
        </div>

        {/* Active Games */}
        {!!(activeRoomsFiltered.length || loading) && (
          <>
            <div className="mb-6 flex items-center gap-6">
              <div className="h-[1px] flex-grow bg-[rgba(255,255,255,0.25)]" />
              <p className="text-2xl font-medium text-[rgba(247,247,247,0.4)]">Active Games</p>
              <div className="h-[1px] flex-grow bg-[rgba(255,255,255,0.25)]" />
            </div>

            <div className="mb-6 grid grid-cols-1 gap-3 pb-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {activeRoomsFiltered.map((room) => (
                <ExploreCard key={room.data.roomId} room={room} chainId={effectiveChainId} />
              ))}

              {loading &&
                Array.from({
                  length: (3 - (activeRoomsFiltered.length % 3)) % 3,
                }).map((_, i) => <ExploreCardSkeleton key={`skeleton-${i}`} />)}
              {loading && activeRoomsFiltered.length % 3 === 0 && (
                <>
                  <ExploreCardSkeleton />
                  <ExploreCardSkeleton />
                  <ExploreCardSkeleton />
                </>
              )}
            </div>
          </>
        )}

        {/* Game Replays */}
        {!!filteredPlayedRooms.length && (
          <>
            <div className="mb-6 flex items-center gap-6">
              <div className="h-[1px] flex-grow bg-[rgba(255,255,255,0.25)]" />
              <p className="text-2xl font-medium text-[rgba(247,247,247,0.4)]">Game Replays</p>
              <div className="h-[1px] flex-grow bg-[rgba(255,255,255,0.25)]" />
            </div>
            <div className="grid grid-cols-1 gap-3 pb-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {filteredPlayedRooms.map((room) => (
                <ExploreCard key={room.data.roomId} room={room} chainId={effectiveChainId} />
              ))}
            </div>
          </>
        )}
        <div ref={bottomRefCallback} className="h-1" />
      </div>
    </DesktopLayout>
  );
};
